import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactSelect, { createFilter, defaultTheme } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import DropdownIndicator from './DropdownIndicator'
import SelectAllMenuList from './SelectAllMenuList'

const DEFAULT_FILTER_OPTION = createFilter()

const BORDER_COLOR = '#d1d5db'
const PLACEHOLDER_COLOR = '#6b7280'
const INDICATOR_COLOR = '#6b7280'
const FONT_SIZE = 14
const MIN_HEIGHT = 36

const toFilterCandidate = option => ({ label: option.label, value: option.value, data: option })

export const SELECT_THEME = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    primary: '#2cc675',
    primary75: '#229f5d',
    primary50: '#ced4da',
    primary25: '#e9ecef',
    neutral20: BORDER_COLOR,
    neutral30: BORDER_COLOR
  }
}

const TRUNCATE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

export const MENU_PORTAL_TARGET = typeof document === 'undefined' ? null : document.body

export const SELECT_STYLES = {
  menu: base => ({
    ...base,
    fontSize: FONT_SIZE,
    zIndex: 9999,
    overflowX: 'hidden'
  }),
  menuList: base => ({
    ...base,
    overflowX: 'hidden'
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  control: (base, state) => {
    const { error, controlBorderColor, controlMinHeight } = state.selectProps
    const border = error ? '#dc3545' : controlBorderColor || base.borderColor
    return {
      ...base,
      'borderColor': border,
      'boxShadow': 'none',
      'fontSize': FONT_SIZE,
      'minHeight': controlMinHeight || MIN_HEIGHT,
      '&:hover': { borderColor: border }
    }
  },
  option: base => ({ ...base, ...TRUNCATE }),
  placeholder: (base, state) => ({
    ...base,
    color: state.selectProps.controlPlaceholderColor || PLACEHOLDER_COLOR
  }),
  singleValue: base => ({ ...base, ...TRUNCATE }),
  multiValue: base => ({ ...base, backgroundColor: '#e5e7eb', borderRadius: 4 }),
  // Cap tag width so a long label truncates instead of filling the control row.
  multiValueLabel: base => ({
    ...base, ...TRUNCATE, maxWidth: '16rem', color: '#1f2937', fontSize: '0.75rem', fontWeight: 500
  }),
  multiValueRemove: base => ({
    ...base,
    'color': '#1f2937',
    'borderRadius': 4,
    ':hover': { backgroundColor: '#d1d5db', color: '#1f2937' }
  }),
  clearIndicator: base => ({
    ...base,
    'color': INDICATOR_COLOR,
    ':hover': { color: INDICATOR_COLOR }
  }),
  indicatorSeparator: (base, state) => {
    if (state.selectProps.isSearchable)
      return base
    return { ...base, display: 'none' }
  }
}

const defaultFormatOptionLabel = option => {
  const titleAttr = typeof option.label === 'string' ? { title: option.label } : {}
  return <span {...titleAttr}>{option.label}</span>
}

const resolvePortalTarget = (target, menuPortal) => {
  if (target)
    return target

  return menuPortal ? MENU_PORTAL_TARGET : null
}

const Select = ({
  options,
  value = null,
  onChange = null,
  placeholder = 'Sélectionner...',
  className = '',
  isMulti = false,
  isClearable = false,
  isDisabled = false,
  isSearchable = true,
  closeMenuOnSelect = !isMulti,
  noOptionsMessage = null,
  error = false,
  filterOption = DEFAULT_FILTER_OPTION,
  showSelectAll = false,
  menuPlacement = 'auto',
  formatOptionLabel = defaultFormatOptionLabel,
  menuPortal = false,
  menuPortalTarget = null,
  creatable,
  components: componentsProp,
  ...rest
}) => {
  const SelectComponent = creatable ? CreatableSelect : ReactSelect
  const handleChange = useCallback(option => {
    if (onChange)
      onChange(option)
  }, [onChange])

  const defaultNoOptionsMessage = () => 'Aucune option disponible'

  const flattenOptionList = useCallback(optList => {
    const flattenOptions = []
    if (!optList)
      return flattenOptions
    optList.forEach(opt => {
      if (opt.options)
        flattenOptions.push(...flattenOptionList(opt.options))
      else if (typeof opt.value !== 'undefined' && opt.value !== null && opt.label)
        flattenOptions.push(opt)
    })
    return flattenOptions
  }, [])

  const selectedOptions = useMemo(() =>
    Array.isArray(value) ? value.filter(Boolean) : [], [value])

  const matchesInput = useCallback((option, inputValue) =>
    !inputValue || filterOption(toFilterCandidate(option), inputValue), [filterOption])

  const notYetSelected = useCallback(optionList => {
    const selectedValues = new Set(selectedOptions.map(option => option.value))
    return optionList.filter(option => !selectedValues.has(option.value))
  }, [selectedOptions])

  const optionsToAdd = useCallback(inputValue => {
    const matchingOptions = flattenOptionList(options).filter(option => matchesInput(option, inputValue))
    return notYetSelected(matchingOptions)
  }, [options, flattenOptionList, matchesInput, notYetSelected])

  const optionsToRemove = useCallback(inputValue =>
    selectedOptions.filter(option => matchesInput(option, inputValue)), [selectedOptions, matchesInput])

  const handleSelectAll = useCallback(inputValue => {
    handleChange([...selectedOptions, ...optionsToAdd(inputValue)])
  }, [selectedOptions, optionsToAdd, handleChange])

  const handleDeselectAll = useCallback(inputValue => {
    handleChange(selectedOptions.filter(option => !matchesInput(option, inputValue)))
  }, [selectedOptions, matchesInput, handleChange])

  const getSelectAllCounts = useCallback(inputValue => ({
    selectable: optionsToAdd(inputValue).length,
    deselectable: optionsToRemove(inputValue).length
  }), [optionsToAdd, optionsToRemove])

  const customComponents = useMemo(() => {
    const base = { DropdownIndicator }
    if (showSelectAll && isMulti)
      base.MenuList = SelectAllMenuList
    return componentsProp ? { ...base, ...componentsProp } : base
  }, [showSelectAll, isMulti, componentsProp])

  const portalTarget = resolvePortalTarget(menuPortalTarget, menuPortal)

  const handlePaste = function handlePaste(event) {
    if (!isMulti)
      return

    const clipboardData = event.clipboardData.getData('text')
    if (!clipboardData)
      return

    const splitStr = clipboardData.includes('\r\n') ? '\r\n' : '\n'
    const pastedLines = clipboardData.split(splitStr)
      .map(line => line.trim())
      .filter(line => line.length > 0)
    if (pastedLines.length === 0)
      return

    event.preventDefault()

    const flattenOptions = flattenOptionList(options)
    const matchedOptions = flattenOptions.filter(option =>
      pastedLines.some(pastedLine => String(option.label).toUpperCase() === pastedLine.toUpperCase())
    )

    if (matchedOptions.length > 0)
      handleChange([...selectedOptions, ...notYetSelected(matchedOptions)])
  }

  return (
    <div onPaste={handlePaste}>
      <SelectComponent
        {...rest}
        className={className}
        closeMenuOnSelect={closeMenuOnSelect}
        components={customComponents}
        error={error}
        filterOption={filterOption}
        formatOptionLabel={formatOptionLabel}
        getSelectAllCounts={getSelectAllCounts}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        menuPortalTarget={portalTarget}
        noOptionsMessage={noOptionsMessage || defaultNoOptionsMessage}
        options={options}
        placeholder={placeholder}
        styles={SELECT_STYLES}
        theme={SELECT_THEME}
        value={value}
        onChange={handleChange}
        onDeselectAll={handleDeselectAll}
        onSelectAll={handleSelectAll}
      />
    </div>
  )
}

Select.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number
        ]).isRequired,
        label: PropTypes.node.isRequired
      }),
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.number
            ]).isRequired,
            label: PropTypes.node.isRequired
          })
        ).isRequired
      })
    ])
  ).isRequired
}

Select.defaultProps = {
  onChange: null
}

export default Select
